// ============================================
// pages/Salon/SalonLeads.js
// Lead management with connector setup
// ============================================
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';
import {
  Users, Plus, Search, Phone, Mail,
  Facebook, Instagram, MessageSquare,
  Eye, CheckCircle, Clock, AlertCircle,
  UserPlus, Loader2, Plug, Save, KeyRound, ExternalLink, RefreshCw, Trash2
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EMPTY_LEAD = {
  name: '',
  phone: '',
  email: '',
  source: 'manual',
  status: 'new',
  notes: '',
  interestedServices: ''
};

const EMPTY_INTEGRATION = {
  facebook_page_id: '',
  facebook_access_token: '',
  instagram_business_id: '',
  instagram_access_token: '',
  whatsapp_business_id: '',
  whatsapp_access_token: '',
  connectedFacebook: false,
  connectedInstagram: false,
};

function maskToken(value) {
  if (!value) return 'Not saved';
  if (value.length <= 10) return value;
  return value.slice(0, 4) + '...' + value.slice(-4);
}

export default function SalonLeads() {
  const { userToken: token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState(EMPTY_LEAD);
  const [integrationForm, setIntegrationForm] = useState(EMPTY_INTEGRATION);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0, facebook: 0, instagram: 0, whatsapp: 0, manual: 0 });

  useEffect(() => {
    loadLeads();
    loadIntegrations();
  }, []);

  const loadLeads = async () => {
    try {
      const res = await fetch(SALON_API_URL + '/leads', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) {
        setLeads(data.data || []);
        calculateStats(data.data || []);
      } else {
        toast.error(data.message || 'Failed to load leads');
      }
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const loadIntegrations = async () => {
    try {
      const res = await fetch(SALON_API_URL + '/leads/integrations', { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) {
        setIntegrationForm(prev => ({ ...prev, ...data.data }));
      }
    } catch (err) {
      toast.error('Failed to load connector setup');
    }
  };

  const calculateStats = (leadsData) => {
    setStats({
      total: leadsData.length,
      new: leadsData.filter(l => l.status === 'new').length,
      contacted: leadsData.filter(l => l.status === 'contacted').length,
      converted: leadsData.filter(l => l.status === 'converted').length,
      facebook: leadsData.filter(l => l.source === 'facebook').length,
      instagram: leadsData.filter(l => l.source === 'instagram').length,
      whatsapp: leadsData.filter(l => l.source === 'whatsapp').length,
      manual: leadsData.filter(l => l.source === 'manual').length,
    });
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(SALON_API_URL + '/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Lead added successfully');
        setShowAddModal(false);
        setFormData(EMPTY_LEAD);
        loadLeads();
      } else {
        toast.error(data.message || 'Failed to add lead');
      }
    } catch {
      toast.error('Failed to add lead');
    }
  };

  const saveIntegrations = async () => {
    setSavingIntegration(true);
    try {
      const payload = {
        facebook_page_id: integrationForm.facebook_page_id,
        facebook_access_token: integrationForm.facebook_access_token,
        instagram_business_id: integrationForm.instagram_business_id,
        instagram_access_token: integrationForm.instagram_access_token,
      };
      const res = await fetch(SALON_API_URL + '/leads/integrations', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to save integrations');
      setIntegrationForm(prev => ({ ...prev, ...data.data, facebook_access_token: payload.facebook_access_token, instagram_access_token: payload.instagram_access_token }));
      toast.success('Connector setup saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save integrations');
    } finally {
      setSavingIntegration(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const res = await fetch(SALON_API_URL + '/leads/' + leadId + '/status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Status updated');
        loadLeads();
      } else {
        toast.error(data.message || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteLead = async (leadId) => {
    try {
      const res = await fetch(SALON_API_URL + '/leads/' + leadId, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      if (data.success) {
        toast.success('Lead deleted');
        loadLeads();
      } else {
        toast.error(data.message || 'Failed to delete lead');
      }
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const filteredLeads = useMemo(() => leads.filter((lead) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = lead.name.toLowerCase().includes(term) || (lead.phone || '').includes(searchTerm) || (lead.email || '').toLowerCase().includes(term);
    const matchesSource = filterSource === 'all' || lead.source === filterSource;
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    return matchesSearch && matchesSource && matchesStatus;
  }), [leads, searchTerm, filterSource, filterStatus]);

  const getSourceIcon = (source) => {
    switch (source) {
      case 'facebook': return <Facebook size={16} className="text-blue-600" />;
      case 'instagram': return <Instagram size={16} className="text-pink-600" />;
      case 'whatsapp': return <MessageSquare size={16} className="text-green-600" />;
      default: return <UserPlus size={16} className="text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
      contacted: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300',
      converted: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300',
      lost: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
    };
    return <span className={"px-2 py-1 rounded-full text-xs font-semibold " + styles[status]}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin text-purple-600" size={48} /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mb-6 space-y-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Lead Management</h1>
            <p className="text-gray-600 dark:text-gray-400">Track leads and save connector settings for Facebook and Instagram.</p>
          </div>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold flex items-center gap-2">
            <Plus size={20} /> Add Lead
          </button>
        </div>

        <div className="grid xl:grid-cols-[1.15fr_0.85fr] gap-6">
          <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow border border-gray-100 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-2">
              <Plug size={18} className="text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Connector Setup</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Save your Meta IDs and long-lived access tokens here. This gives the leads module a real connection setup surface.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/10 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Facebook size={18} className="text-blue-600" /><p className="font-bold text-gray-900 dark:text-white">Facebook</p></div>
                  <span className={integrationForm.connectedFacebook ? 'px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300'}>{integrationForm.connectedFacebook ? 'Connected' : 'Not Connected'}</span>
                </div>
                <input value={integrationForm.facebook_page_id} onChange={(e) => setIntegrationForm({ ...integrationForm, facebook_page_id: e.target.value, connectedFacebook: false })} placeholder="Facebook Page ID" className="w-full px-4 py-2.5 rounded-xl border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white" />
                <textarea value={integrationForm.facebook_access_token} onChange={(e) => setIntegrationForm({ ...integrationForm, facebook_access_token: e.target.value, connectedFacebook: false })} placeholder="Facebook long-lived access token" rows="3" className="w-full px-4 py-2.5 rounded-xl border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white resize-none" />
                <p className="text-xs text-gray-500 dark:text-gray-400">Saved token: {maskToken(integrationForm.facebook_access_token)}</p>
              </div>
              <div className="rounded-2xl border border-pink-200 dark:border-pink-900 bg-pink-50 dark:bg-pink-900/10 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Instagram size={18} className="text-pink-600" /><p className="font-bold text-gray-900 dark:text-white">Instagram</p></div>
                  <span className={integrationForm.connectedInstagram ? 'px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300'}>{integrationForm.connectedInstagram ? 'Connected' : 'Not Connected'}</span>
                </div>
                <input value={integrationForm.instagram_business_id} onChange={(e) => setIntegrationForm({ ...integrationForm, instagram_business_id: e.target.value, connectedInstagram: false })} placeholder="Instagram Business ID" className="w-full px-4 py-2.5 rounded-xl border border-pink-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white" />
                <textarea value={integrationForm.instagram_access_token} onChange={(e) => setIntegrationForm({ ...integrationForm, instagram_access_token: e.target.value, connectedInstagram: false })} placeholder="Instagram long-lived access token" rows="3" className="w-full px-4 py-2.5 rounded-xl border border-pink-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-white resize-none" />
                <p className="text-xs text-gray-500 dark:text-gray-400">Saved token: {maskToken(integrationForm.instagram_access_token)}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button onClick={saveIntegrations} disabled={savingIntegration} className="px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold flex items-center gap-2 disabled:opacity-60">{savingIntegration ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Connector Setup</button>
              <button onClick={loadIntegrations} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-700 dark:text-slate-200 flex items-center gap-2"><RefreshCw size={16} /> Reload</button>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl shadow border border-gray-100 dark:border-slate-700 space-y-4">
            <div className="flex items-center gap-2"><KeyRound size={18} className="text-amber-600" /><h2 className="text-lg font-bold text-gray-900 dark:text-white">Meta Setup Notes</h2></div>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
              <p>1. Create a Meta app and enable Facebook/Instagram permissions.</p>
              <p>2. Get the Facebook Page ID and Instagram Business ID from your connected business assets.</p>
              <p>3. Generate long-lived access tokens and paste them here.</p>
              <p>4. This saves the connector setup inside the salon backend so your leads module knows which accounts belong to this salon.</p>
            </div>
            <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-purple-600 hover:text-purple-700">
              Open Meta Developers <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600 dark:text-gray-400">Total Leads</p><p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p></div><Users className="text-purple-600" size={32} /></div></div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600 dark:text-gray-400">New</p><p className="text-2xl font-bold text-blue-600">{stats.new}</p></div><Clock className="text-blue-600" size={32} /></div></div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600 dark:text-gray-400">Contacted</p><p className="text-2xl font-bold text-yellow-600">{stats.contacted}</p></div><Phone className="text-yellow-600" size={32} /></div></div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600 dark:text-gray-400">Converted</p><p className="text-2xl font-bold text-green-600">{stats.converted}</p></div><CheckCircle className="text-green-600" size={32} /></div></div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} /><input type="text" placeholder="Search leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white" /></div>
            <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"><option value="all">All Sources</option><option value="facebook">Facebook</option><option value="instagram">Instagram</option><option value="whatsapp">WhatsApp</option><option value="manual">Manual</option></select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white"><option value="all">All Status</option><option value="new">New</option><option value="contacted">Contacted</option><option value="converted">Converted</option><option value="lost">Lost</option></select>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-700"><tr><th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Name</th><th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Contact</th><th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Source</th><th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Status</th><th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th><th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th></tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {filteredLeads.length === 0 ? <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"><AlertCircle className="mx-auto mb-2" size={48} /><p>No leads found</p></td></tr> : filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                  <td className="px-6 py-4"><div className="font-semibold text-gray-900 dark:text-white">{lead.name}</div></td>
                  <td className="px-6 py-4"><div className="text-sm text-gray-600 dark:text-gray-400">{lead.phone && <div className="flex items-center gap-1"><Phone size={14} />{lead.phone}</div>}{lead.email && <div className="flex items-center gap-1"><Mail size={14} />{lead.email}</div>}</div></td>
                  <td className="px-6 py-4"><div className="flex items-center gap-2">{getSourceIcon(lead.source)}<span className="text-sm capitalize">{lead.source}</span></div></td>
                  <td className="px-6 py-4">{getStatusBadge(lead.status)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{new Date(lead.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4"><div className="flex items-center gap-2"><button onClick={() => { setSelectedLead(lead); setShowViewModal(true); }} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-600 rounded" title="View"><Eye size={18} className="text-blue-600" /></button><button onClick={() => deleteLead(lead.id)} className="p-1 hover:bg-gray-100 dark:hover:bg-slate-600 rounded" title="Delete"><Trash2 size={18} className="text-red-600" /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"><div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full p-6"><h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Add New Lead</h2><form onSubmit={handleAddLead} className="space-y-4"><input type="text" placeholder="Name *" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" /><input type="tel" placeholder="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" /><input type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" /><select value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"><option value="manual">Manual</option><option value="facebook">Facebook</option><option value="instagram">Instagram</option><option value="whatsapp">WhatsApp</option></select><textarea placeholder="Notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white" rows="3" /><div className="flex gap-2"><button type="submit" className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold">Add Lead</button><button type="button" onClick={() => { setShowAddModal(false); setFormData(EMPTY_LEAD); }} className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-900 dark:text-white rounded-lg font-semibold">Cancel</button></div></form></div></div>}

      {showViewModal && selectedLead && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"><div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full p-6"><h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Lead Details</h2><div className="space-y-3"><div><p className="text-sm text-gray-600 dark:text-gray-400">Name</p><p className="font-semibold text-gray-900 dark:text-white">{selectedLead.name}</p></div>{selectedLead.phone && <div><p className="text-sm text-gray-600 dark:text-gray-400">Phone</p><p className="font-semibold text-gray-900 dark:text-white">{selectedLead.phone}</p></div>}{selectedLead.email && <div><p className="text-sm text-gray-600 dark:text-gray-400">Email</p><p className="font-semibold text-gray-900 dark:text-white">{selectedLead.email}</p></div>}<div><p className="text-sm text-gray-600 dark:text-gray-400">Source</p><div className="flex items-center gap-2">{getSourceIcon(selectedLead.source)}<span className="font-semibold capitalize">{selectedLead.source}</span></div></div><div><p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Status</p><select value={selectedLead.status} onChange={(e) => updateLeadStatus(selectedLead.id, e.target.value)} className="w-full px-4 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"><option value="new">New</option><option value="contacted">Contacted</option><option value="converted">Converted</option><option value="lost">Lost</option></select></div>{selectedLead.notes && <div><p className="text-sm text-gray-600 dark:text-gray-400">Notes</p><p className="text-gray-900 dark:text-white">{selectedLead.notes}</p></div>}</div><button onClick={() => setShowViewModal(false)} className="w-full mt-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-900 dark:text-white rounded-lg font-semibold">Close</button></div></div>}
    </div>
  );
}
